import { useMemo } from "react";
import { parseDateOrNull } from "../utilities/tachographDates";
import { useAddInState } from "@/components/AddInStateProvider";
import { useDiagnosticsBySearch } from "@/hooks/diagnostic/useDiagnosticsBySearch";
import { useLatestStatusData } from "@/hooks/statusData/useLatestStatusData";
import { Preset } from "@/models/Preset";

const DIAGNOSTIC_LIMIT = 500;

const REFRESH_INTERVAL_MS = 60000;

/**
 * When each vehicle last reported a tachograph reading. Searched by the full word: "Tacho"
 * mostly finds tachometer diagnostics ("Tachometer signal output"), which say nothing about
 * the tachograph. The generated diagnostic id is not matched either — that made unrelated
 * readings count as a tachograph signal.
 */
export function useTachographSignals(deviceIds: string[]) {
  const { groups, groupFilterCondition } = useAddInState();
  const { data: diagnostics = [] } = useDiagnosticsBySearch("Tachograph", false, DIAGNOSTIC_LIMIT);

  const preset = useMemo<Preset | null>(() => {
    const diagnosticIds = [
      ...new Set(
        diagnostics
          .filter(diagnostic => /tachograph/i.test(diagnostic.name ?? ""))
          .map(diagnostic => diagnostic.id)
          .filter((id): id is string => !!id),
      ),
    ];

    if (diagnosticIds.length === 0) return null;

    return {
      id: "tachograph-tacho-diagnostics",
      columns: diagnosticIds.map(id => ({ diagnostic: { id } })),
    };
  }, [diagnostics]);

  const { data: latestStatus } = useLatestStatusData(
    preset,
    deviceIds,
    groups,
    groupFilterCondition,
    REFRESH_INTERVAL_MS,
    true,
  );

  return useMemo(() => {
    const signalByDeviceId = new Map<string, Date | null>(deviceIds.map(id => [id, null]));

    for (const status of latestStatus?.data ?? []) {
      const deviceId = status.device?.id;
      if (!deviceId) continue;

      const timestamp =
        parseDateOrNull(status.dateTime) ?? parseDateOrNull(status.sensorData?.[0]?.lastUpdateTime);
      if (!timestamp) continue;

      const previous = signalByDeviceId.get(deviceId);
      if (!previous || timestamp > previous) {
        signalByDeviceId.set(deviceId, timestamp);
      }
    }

    return signalByDeviceId;
  }, [deviceIds, latestStatus]);
}
